import { useCallback, useEffect, useRef, useState } from "react";

interface MicrophoneLevelMonitorOptions {
  smoothing?: number;
  boost?: number;
}

interface MicrophoneLevelMonitor {
  level: number;
  status: "initializing" | "monitoring" | "error" | "stopped";
  error: string | null;
  restart(): Promise<void>;
  stop(): void;
}

export const useMicrophoneLevelMonitor = ({
  smoothing = 0.8,
  boost = 1.5,
}: MicrophoneLevelMonitorOptions = {}): MicrophoneLevelMonitor => {
  const [level, setLevel] = useState(0);
  const [status, setStatus] = useState<MicrophoneLevelMonitor["status"]>("initializing");
  const [error, setError] = useState<string | null>(null);

  const streamRef = useRef<MediaStream | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const previousLevelRef = useRef(0);
  const isMountedRef = useRef(false);

  const cleanup = useCallback(() => {
    const processor = processorRef.current;
    if (processor) {
      processor.disconnect();
      processor.onaudioprocess = null;
      processorRef.current = null;
    }

    const stream = streamRef.current;
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    const audioContext = audioContextRef.current;
    if (audioContext) {
      audioContext.close().catch(() => {});
      audioContextRef.current = null;
    }

    if (isMountedRef.current) {
      setLevel(0);
      setStatus("stopped");
      previousLevelRef.current = 0;
    }
  }, []);

  const startMonitoring = useCallback(async () => {
    isMountedRef.current = true;
    const clampedSmoothing = Math.min(0.99, Math.max(0, smoothing));

    if (!navigator.mediaDevices?.getUserMedia) {
      setError("Microphone access is not supported in this browser");
      setStatus("error");
      return;
    }

    cleanup();
    setError(null);
    setStatus("initializing");

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const AudioContextClass =
        window.AudioContext ||
        (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!AudioContextClass) {
        throw new Error("AudioContext is not supported in this browser");
      }
      const audioContext = new AudioContextClass();
      await audioContext.resume();

      const source = audioContext.createMediaStreamSource(stream);
      const processor = audioContext.createScriptProcessor(1024, 1, 1);
      const outputGain = audioContext.createGain();
      outputGain.gain.value = 0;

      processor.onaudioprocess = event => {
        if (!isMountedRef.current) {
          return;
        }

        const input = event.inputBuffer.getChannelData(0);
        let sum = 0;
        for (let index = 0; index < input.length; index += 1) {
          const sample = input[index];
          sum += sample * sample;
        }

        const rms = Math.sqrt(sum / input.length);
        // Apply logarithmic scaling for more natural perception
        // RMS values typically range from 0 to ~0.3 for normal speech
        const scaledRms = Math.log10(rms * 100 + 1) / 2; // Log scale with offset
        const normalized = Math.min(1, Math.max(0, scaledRms * boost));
        const smoothedLevel =
          normalized * (1 - clampedSmoothing) + previousLevelRef.current * clampedSmoothing;
        previousLevelRef.current = smoothedLevel;
        setLevel(smoothedLevel);
      };

      source.connect(processor);
      processor.connect(outputGain);
      outputGain.connect(audioContext.destination);

      audioContextRef.current = audioContext;
      streamRef.current = stream;
      processorRef.current = processor;

      if (isMountedRef.current) {
        setStatus("monitoring");
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to access microphone";
      if (isMountedRef.current) {
        setError(message);
        setStatus("error");
      }
    }
  }, [boost, cleanup, smoothing]);

  useEffect(() => {
    isMountedRef.current = true;
    startMonitoring();

    return () => {
      isMountedRef.current = false;
      cleanup();
    };
  }, [cleanup, startMonitoring]);

  return {
    level,
    status,
    error,
    restart: startMonitoring,
    stop: cleanup,
  };
};

export default useMicrophoneLevelMonitor;
